import { Helmet } from "react-helmet-async";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { 
  ArrowRight, 
  Calendar,
  MessageSquare,
  Heart,
  Users,
  Shield,
  FileText
} from "lucide-react";

const TodaysMeetHistory = () => {
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "TodaysMeet History - Giving Every Student a Voice for 10 Years",
    "description": "The complete history of TodaysMeet, the classroom backchannel chat that empowered millions of students to participate in discussions before shutting down in 2018.",
    "author": {
      "@type": "Organization",
      "name": "Chill Study Work"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Chill Study Work",
      "url": "https://chillstudy.work"
    },
    "datePublished": "2024-01-01",
    "dateModified": "2024-01-01",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://chillstudy.work/todaysmeet/history/"
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>TodaysMeet History - Giving Every Student a Voice | CSW</title>
        <meta name="description" content="The complete history of TodaysMeet, the classroom backchannel chat that empowered millions of students to participate in discussions before shutting down in 2018." />
        <meta name="keywords" content="TodaysMeet history, TodaysMeet shutdown, James Socol, classroom backchannel history, student voice tool" />
        <link rel="canonical" href="https://chillstudy.work/todaysmeet/history/" />
        <meta property="og:title" content="TodaysMeet History - Giving Every Student a Voice" />
        <meta property="og:description" content="The complete history of TodaysMeet, from 2008 creation to 2018 shutdown." />
        <meta property="og:url" content="https://chillstudy.work/todaysmeet/history/" />
        <meta property="og:type" content="article" />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      </Helmet>

      <Header />

      <main className="pt-24 pb-16">
        {/* Hero Section */}
        <section className="container mx-auto px-4 mb-16">
          <div className="max-w-4xl mx-auto">
            <Link to="/todaysmeet/" className="inline-flex items-center gap-2 text-primary hover:underline mb-6">
              ← Back to TodaysMeet Alternative
            </Link>
            <div className="inline-flex items-center gap-2 bg-teal-500/10 text-teal-500 px-4 py-2 rounded-full text-sm font-medium mb-6 ml-4">
              <MessageSquare className="w-4 h-4" />
              2008 - 2018
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              TodaysMeet: A Labor of Love
            </h1>
            <p className="text-xl text-muted-foreground leading-relaxed">
              How one developer's passion project gave millions of students a digital voice 
              in classroom discussions for a decade.
            </p>
          </div>
        </section>

        {/* Timeline Section */}
        <section className="container mx-auto px-4 mb-16">
          <div className="max-w-4xl mx-auto">
            <div className="space-y-8">
              {/* 2008: Creation */}
              <div className="bg-card rounded-2xl p-8 border border-border/50 shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-lg bg-green-500/10 text-green-500">
                    <Calendar className="w-5 h-5" />
                  </div>
                  <span className="text-sm font-medium text-green-500">2008</span>
                </div>
                <h2 className="text-2xl font-bold mb-4">The Beginning</h2>
                <p className="text-muted-foreground mb-4">
                  TodaysMeet was created in 2008 by <strong>James Socol</strong>, a developer passionate about education. 
                  He built it as a simple backchannel chat platform for classrooms – essentially a one-man operation 
                  that started as a free tool and remained free throughout its life.
                </p>
                <p className="text-muted-foreground">
                  The concept was straightforward: create a space where students could type questions, comments, 
                  or answers in real time during class, giving everyone – especially shy students – a digital voice.
                </p>
              </div>

              {/* Growth Phase */}
              <div className="bg-card rounded-2xl p-8 border border-border/50 shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-lg bg-blue-500/10 text-blue-500">
                    <Users className="w-5 h-5" />
                  </div>
                  <span className="text-sm font-medium text-blue-500">Early 2010s</span>
                </div>
                <h2 className="text-2xl font-bold mb-4">Becoming a Classroom Staple</h2>
                <p className="text-muted-foreground mb-4">
                  TodaysMeet gained popularity among teachers and presenters who wanted to engage their audience. 
                  Within a few years, it became <strong>the</strong> go-to solution for classroom backchannel 
                  discussions and conference Q&A sessions.
                </p>
                <div className="bg-muted rounded-lg p-6 mb-4">
                  <h3 className="font-semibold mb-3">What Made TodaysMeet Special:</h3>
                  <ul className="space-y-2 text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <MessageSquare className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                      <span><strong>Instant setup:</strong> Create a room in seconds with no registration required for participants</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <MessageSquare className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                      <span><strong>Twitter-like format:</strong> Originally 140-character limit, keeping messages concise</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <MessageSquare className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                      <span><strong>Ephemeral rooms:</strong> Chats disappeared after the room expired, addressing privacy concerns</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <MessageSquare className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                      <span><strong>Device-agnostic:</strong> Worked on any browser, including older computers and student phones</span>
                    </li>
                  </ul>
                </div>
                <blockquote className="border-l-4 border-primary pl-4 italic text-muted-foreground">
                  "TodaysMeet gave every student a digital voice in classroom discussions." — Educators describing TodaysMeet's impact
                </blockquote>
              </div>

              {/* The Appeal */}
              <div className="bg-card rounded-2xl p-8 border border-border/50 shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-lg bg-purple-500/10 text-purple-500">
                    <Heart className="w-5 h-5" />
                  </div>
                  <span className="text-sm font-medium text-purple-500">A Decade of Impact</span>
                </div>
                <h2 className="text-2xl font-bold mb-4">Why Educators Loved It</h2>
                <p className="text-muted-foreground mb-4">
                  The no-frills, text-only approach kept the focus on content rather than fancy features. 
                  In a typical use case, a teacher would pose a question verbally, and all students – even 
                  the shy ones – could respond simultaneously in TodaysMeet.
                </p>
                <p className="text-muted-foreground mb-4">
                  It also excelled during presentations and assemblies, where audience members could submit 
                  questions without interrupting the speaker. Teachers loved that it was simple, worked everywhere, 
                  and required no complex setup or IT approval.
                </p>
                <p className="text-muted-foreground">
                  Notably, Socol maintained TodaysMeet as a "labor of love" on his own servers, with minimal 
                  monetization (a paid tier was introduced late, but it stayed largely free for teachers).
                </p>
              </div>

              {/* The Struggle */}
              <div className="bg-card rounded-2xl p-8 border border-border/50 shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-lg bg-yellow-500/10 text-yellow-500">
                    <Shield className="w-5 h-5" />
                  </div>
                  <span className="text-sm font-medium text-yellow-500">2018</span>
                </div>
                <h2 className="text-2xl font-bold mb-4">The Sustainability Challenge</h2>
                <p className="text-muted-foreground mb-4">
                  Running a free service for millions of messages requires significant time for moderation, 
                  updates, and server maintenance. With new demands like <strong>GDPR</strong> (the EU privacy regulation 
                  that took effect in 2018), the overhead to keep TodaysMeet compliant became substantial.
                </p>
                <p className="text-muted-foreground">
                  Socol had a full-time job elsewhere by then, and TodaysMeet didn't generate enough income 
                  to justify the work needed. After a decade, he had to make the "hard decision" to close it.
                </p>
              </div>

              {/* Shutdown */}
              <div className="bg-card rounded-2xl p-8 border border-border/50 shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-lg bg-red-500/10 text-red-500">
                    <Calendar className="w-5 h-5" />
                  </div>
                  <span className="text-sm font-medium text-red-500">June 16, 2018</span>
                </div>
                <h2 className="text-2xl font-bold mb-4">The Final Day</h2>
                <p className="text-muted-foreground mb-4">
                  On June 16, 2018, TodaysMeet was shut down permanently. Socol announced the decision about 
                  a month prior on the TodaysMeet blog, citing "lack of personal time to maintain this labor of love."
                </p>
                <blockquote className="border-l-4 border-destructive pl-4 italic text-muted-foreground mb-4">
                  "It's been a hard day… one of my favorite free educational tools is going away." — Ed-tech blogger reaction
                </blockquote>
                <p className="text-muted-foreground">
                  Users were given notice to save important transcripts before the service went offline. 
                  There was no acquisition or successor – it was a straight closure due to sustainability issues.
                </p>
              </div>

              {/* Legacy */}
              <div className="bg-card rounded-2xl p-8 border border-border/50 shadow-lg">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 rounded-lg bg-primary/10 text-primary">
                    <Heart className="w-5 h-5" />
                  </div>
                  <span className="text-sm font-medium text-primary">Legacy</span>
                </div>
                <h2 className="text-2xl font-bold mb-4">Impact and Legacy</h2>
                <p className="text-muted-foreground mb-4">
                  TodaysMeet popularized the concept of a classroom backchannel. Today, many modern platforms 
                  (Zoom, Teams, etc.) include chat or Q&A functions that serve a similar purpose – but TodaysMeet 
                  did it first and without barriers.
                </p>
                <p className="text-muted-foreground mb-4">
                  Teachers became more aware of the value of student voice thanks to tools like TodaysMeet. 
                  Its existence and demise also underscored a broader point: free, one-person-run tools can 
                  have outsized impact, but they can disappear if not properly supported.
                </p>
                <blockquote className="border-l-4 border-primary pl-4 italic text-muted-foreground">
                  "You will NOT be forgotten." — Educator's tribute tweet to TodaysMeet's creator
                </blockquote>
              </div>
            </div>
          </div>
        </section>

        {/* External References */}
        <section className="container mx-auto px-4 mb-16">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold mb-6">References & Further Reading</h2>
            <div className="space-y-3">
              <a 
                href="https://www.freetech4teachers.com/2018/05/todaysmeet-is-shutting-down.html" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-4 bg-card rounded-lg border border-border/50 hover:border-primary/50 transition-colors"
              >
                <FileText className="w-5 h-5 text-primary" />
                <span>Free Tech 4 Teachers: TodaysMeet Is Shutting Down</span>
                <ArrowRight className="w-4 h-4 ml-auto" />
              </a>
              <a 
                href="https://mguhlin.org/2018/05/02/big-changes-for-todaysmeet-and-dotstorming-users/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-4 bg-card rounded-lg border border-border/50 hover:border-primary/50 transition-colors"
              >
                <FileText className="w-5 h-5 text-primary" />
                <span>Miguel Guhlin: Big Changes for TodaysMeet Users</span>
                <ArrowRight className="w-4 h-4 ml-auto" />
              </a>
              <a 
                href="https://www.cultofpedagogy.com/backchannel-chat/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-3 p-4 bg-card rounded-lg border border-border/50 hover:border-primary/50 transition-colors"
              >
                <FileText className="w-5 h-5 text-primary" />
                <span>Cult of Pedagogy: Backchannel Chat in the Classroom</span>
                <ArrowRight className="w-4 h-4 ml-auto" />
              </a>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">Continue Empowering Student Voices</h2>
            <p className="text-xl text-muted-foreground mb-8">
              TodaysMeet showed how powerful it is to give every student a voice. 
              Continue that mission with CSW's interactive study rooms.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="text-lg px-8">
                <a href="https://app.chillstudy.work" target="_blank" rel="noopener noreferrer">
                  Try CSW Free
                  <ArrowRight className="w-5 h-5 ml-2" />
                </a>
              </Button>
              <Button asChild size="lg" variant="outline" className="text-lg px-8">
                <Link to="/alternatives/">
                  View All Alternatives
                </Link>
              </Button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default TodaysMeetHistory;
